import { memo, type CSSProperties } from 'react';
import { Avatar, usePortraitImg } from '../../components/Avatar';
import { Icon } from '../../components/Icon';
import { ShabbatCandles } from '../../components/ShabbatCandles';
import { lifeYears } from '../../domain/dates';
import { displayName } from '../../domain/names';
import type { MemoryDot, MemoryIndicator } from '../../domain/memoryDots';
import type { OccasionKind } from '../../domain/occasions';
import type { Lang, PersonWithNames } from '../../domain/types';
import { NODE_H, NODE_W } from './layout';
import type { ZoomBand } from './zoom';

export interface BubbleProps {
  person: PersonWithNames;
  lang: Lang;
  x: number;
  y: number;
  zoom: ZoomBand;
  isMe: boolean;
  isSelected: boolean;
  dimmed: boolean;
  memory: MemoryIndicator;
  showPending: boolean;
  /**
   * Added but not yet recognised by the family. Drawn dimmed with a dashed rim
   * for *everyone*, not only reviewers: the tree stays whole while the question
   * is open, and it is obvious which circle the question is about.
   */
  awaitingRecognition?: boolean;
  reducedMotion: boolean;
  floatSeed: number;
  /**
   * Nobody has signed in as this person: no account has claimed the circle.
   * Someone may still be looking after it, but there is no-one *behind* it —
   * which is what the marker on the circle says.
   */
  unclaimed: boolean;
  /** This viewer follows this person closely. */
  starred: boolean;
  /** Away on a visit today (World tab). */
  visiting?: boolean;
  /** It is Shabbat right now where this person is. */
  shabbat?: boolean;
  labelShabbat?: string;
  /** Their day today: a birthday, an anniversary, a yahrzeit (Coming up). */
  occasion?: OccasionKind;
  labelOccasion?: string;
  /** 1 = default size; grows with fresh activity and settles back (admin-tunable). */
  nodeScale: number;
  onSelect: (id: string) => void;
  labelStories: string;
  labelPending: string;
  labelYou: string;
  labelUnclaimed: string;
  /** "Of blessed memory" — the tooltip on a deceased relative's mark. */
  labelMemorial: string;
  /**
   * The mark itself: ז״ל, the two Hebrew letters a family writes after the name
   * of someone who has died. Kept in the message files rather than written here
   * so a family can change what their tree says.
   */
  memorialMark: string;
  labelBadgeStory: string;
  labelBadgePhoto: string;
  labelBadgeMore: (n: number) => string;
}

/**
 * One person in the tree. The gentle wobble is a decoration around the assigned
 * position — never a physics simulation (section 9.2) — and is removed entirely
 * in reduced-motion mode. Each circle drifts on its own path and tempo (derived
 * from a stable per-person seed) so the tree feels alive rather than metronomic.
 */
export const PersonBubble = memo(function PersonBubble({
  person, lang, x, y, zoom, isMe, isSelected, dimmed, memory, showPending, awaitingRecognition = false, reducedMotion, floatSeed, unclaimed, starred, visiting = false, shabbat = false, labelShabbat, occasion, labelOccasion, nodeScale,
  onSelect, labelStories, labelPending, labelYou, labelUnclaimed, labelMemorial, memorialMark, labelBadgeStory, labelBadgePhoto, labelBadgeMore,
}: BubbleProps) {
  const name = displayName(person, lang);
  const years = lifeYears(person.birth, person.death, person.status === 'deceased', lang);
  /*
   * Zoomed far out, the small trimmings on a circle stop being readable, so
   * they are dropped. What must NOT change is the circle itself: the avatar
   * used to switch to a smaller size class here, which made every node in the
   * tree jump about a third smaller the moment you crossed this threshold. The
   * circle is now one size always, and the canvas transform does the shrinking
   * — so zooming out is a smooth glide, not a step.
   */
  const veryFar = zoom === 'veryFar';
  const far = veryFar || zoom === 'far';
  const deceased = person.status === 'deceased';
  const hasPhoto = !!person.portraitUrl;

  // Three wobble paths, picked by seed, each with its own amplitude and tempo.
  const path = Math.floor(floatSeed * 3);
  const style: CSSProperties & Record<string, string | number> = {
    transform: `translate3d(${x - NODE_W / 2}px, ${y - NODE_H / 2}px, 0)`,
    width: NODE_W,
    height: NODE_H,
    '--wobble-delay': `${(floatSeed * -11).toFixed(2)}s`,
    '--wobble-dur': `${(8 + floatSeed * 6).toFixed(2)}s`,
    '--wobble-amp': `${(3 + floatSeed * 3).toFixed(1)}px`,
    '--wobble-rot': `${(0.6 + floatSeed * 0.9).toFixed(2)}deg`,
  };

  const ariaParts = [name];
  if (years) ariaParts.push(years);
  if (isMe) ariaParts.push(labelYou);
  if (memory.total) ariaParts.push(labelStories);
  if (showPending || awaitingRecognition) ariaParts.push(labelPending);
  if (deceased) ariaParts.push(labelMemorial);
  else if (unclaimed) ariaParts.push(labelUnclaimed);
  if (starred) ariaParts.push('★');
  if (person.locked) ariaParts.push('🔒');
  if (visiting) ariaParts.push('🧳');
  if (shabbat) ariaParts.push(labelShabbat ?? '🕯️');
  if (occasion && labelOccasion) ariaParts.push(labelOccasion);

  return (
    <button
      type="button"
      data-node={person.id}
      className={[
        'bubble',
        isMe ? 'is-me' : '',
        isSelected ? 'is-selected' : '',
        dimmed ? 'is-dim' : '',
        awaitingRecognition ? 'is-unrecognised' : '',
        deceased ? 'is-deceased' : '',
        hasPhoto ? 'has-photo' : '',
        starred ? 'is-starred' : '',
        shabbat ? 'is-shabbat' : '',
        occasion ? `has-occasion is-${occasion}` : '',
        reducedMotion ? 'no-float' : `floaty wobble-${path}`,
      ].join(' ')}
      style={style}
      onClick={() => onSelect(person.id)}
      aria-label={ariaParts.join(' · ')}
      aria-pressed={isSelected}
    >
      <span className="bubble-drift">
        <span className="bubble-avatar-wrap" style={{ '--node-scale': nodeScale.toFixed(3) } as CSSProperties}>
          <span className="bubble-halo" aria-hidden="true" style={{ '--halo': `var(--hue-${person.portraitHue % 16})` } as CSSProperties} />
          {shabbat && (
            // Behind the photo, peeking out of the top-left rim. Colour = the ring's meaning: teal for you,
            // gold for someone you starred, otherwise the person's own hue (the same one as their halo).
            <span className="bubble-candles" aria-hidden="true" title={labelShabbat} style={{ '--candle': isMe ? 'var(--primary)' : starred ? 'var(--star)' : `var(--hue-${person.portraitHue % 16})` } as CSSProperties}>
              {/* From far off the flames are a few pixels tall: nothing to see flicker, and every flame is an animation. */}
              <ShabbatCandles still={reducedMotion || far} />
            </span>
          )}
          <Avatar person={person} lang={lang} size="tree" full={zoom === 'close'} />
          {memory.totalAll > 0 && <MemoryBadges memory={memory} reducedMotion={reducedMotion} labelStory={labelBadgeStory} labelPhoto={labelBadgePhoto} labelMore={labelBadgeMore} />}
          {(showPending || awaitingRecognition) && (
            <span className="bubble-flag" title={labelPending}>
              <Icon name="clock" />
            </span>
          )}
          {/*
            * One badge, two meanings, in the same place on the circle.
            *
            * Someone who has passed away: ז״ל — the two letters a Jewish family
            * already writes after the name, "of blessed memory". No picture of a
            * grave, no candle (that one means a yahrzeit is *today*), just the
            * mark. It is used because "nobody has signed in as this person" is
            * true of them and always will be, and saying it that way would be
            * both useless and unkind.
            *
            * Otherwise, a circle no account has claimed: a person drawn as a
            * dotted outline. Shown whether or not there is a photo — it is about
            * who is behind the circle, not what is on it.
            */}
          {deceased && !far && (
            <span className="bubble-unclaimed is-memorial" title={labelMemorial} aria-hidden="true">
              <span className="memorial-mark" dir="rtl" lang="he">{memorialMark}</span>
            </span>
          )}
          {!deceased && unclaimed && !far && (
            <span className="bubble-unclaimed" title={labelUnclaimed} aria-hidden="true">
              <Icon name="person" />
            </span>
          )}
          {person.locked && !far && (
            <span className="bubble-lock" aria-hidden="true">
              <Icon name="lock" />
            </span>
          )}
          {visiting && !far && (
            <span className="bubble-visit" aria-hidden="true">
              <Icon name="suitcase" />
            </span>
          )}
          {starred && (
            <span className="bubble-star" aria-hidden="true">
              <Icon name="star" />
            </span>
          )}
          {occasion && !far && (
            // Their day today. Sits on the left rim, the one spot nothing else uses (badges walk the right side,
            // the star and candles the top, the suitcase and lock the bottom corners).
            <span className={`bubble-occasion is-${occasion}`} aria-hidden="true" title={labelOccasion}>
              <Icon name={occasion === 'birthday' ? 'cake' : occasion === 'anniversary' ? 'heart' : 'candle'} />
            </span>
          )}
        </span>
        {!veryFar && (
          <span className="bubble-label">
            <bdi className="bubble-name">{name}</bdi>
            {!far && years ? <span className="bubble-years">{years}</span> : null}
          </span>
        )}
      </span>
    </button>
  );
});

/**
 * The arc the photo badges may use, in degrees: 0 is 3 o'clock, 90 is the
 * bottom, −90 is the very top. They walk clockwise from `BADGE_FROM`, and no
 * badge's trailing edge may pass `BADGE_TO`.
 *
 * **The rim is shared.** The Shabbat candles rise out of the very top (about
 * −110° to −70°), the star sits on the top-left corner (about −147° to −123°)
 * and the day's own mark — a heart on an anniversary, a cake on a birthday, a
 * flame on a yahrzeit — on the left rim (about 165° to 195°). So the badges get
 * the right side and the bottom and stop short of all three.
 *
 * Before this they simply kept walking: eight badges at their newborn 30px step
 * about 37° each, which swept 300° of the circle and buried the heart, the star
 * and the candles underneath a week's photographs. The numbers are deliberately
 * generous at the settled size — eight 16px badges step 22° and all eight still
 * fit — and tight while they are new, which is exactly when there are few of
 * them and they are the point.
 *
 * (The one neighbour still inside the arc is the dotted "nobody has claimed
 * this circle" mark on the bottom-left corner, at about 135°. It has always
 * shared this space; narrowing the arc far enough to clear it as well would
 * leave room for four new badges out of eight, which costs more than it saves.)
 */
const BADGE_FROM = -40;
const BADGE_TO = 152;

/**
 * Memory badges (section 9.7): every new story or photo sits as a small round
 * badge on the rim of the circle, walking clockwise from about one o'clock. A
 * badge is born large and shrinks over 14 days to a permanent small mark. Story
 * badges carry the story icon; photo badges show the photo.
 *
 * Whatever does not fit inside the arc above is **not drawn at all**, and the
 * last badge that did fit carries the count of everything behind it — the same
 * promise the eight-badge cap already made, now answering "is there room?" as
 * well as "are there too many?". The count is in the accessible label too, so
 * size and colour are never the only signal.
 */
export function MemoryBadges({
  memory, reducedMotion, labelStory, labelPhoto, labelMore, radius = 52, shrink = 1,
}: {
  memory: MemoryIndicator; reducedMotion: boolean;
  labelStory: string; labelPhoto: string; labelMore: (n: number) => string;
  /** Rim radius of the circle underneath — 52 for a tree bubble, smaller for a globe pin. */
  radius?: number;
  /** Badge sizes are tuned to the tree's circle; a smaller circle wears smaller badges. */
  shrink?: number;
}) {
  // Each step is sized so neighbours just touch; a badge is centred on its own angle.
  const placed: Array<{ dot: MemoryDot; size: number; at: number }> = [];
  let angle = BADGE_FROM;
  for (const d of memory.dots) {
    const size = d.sizePx * shrink;
    const step = ((size + 4) / radius) * (180 / Math.PI);
    if (angle + step / 2 > BADGE_TO) break;
    placed.push({ dot: d, size, at: angle });
    angle += step;
  }
  // A circle that has something to say always says it, even if the arc could not hold one badge.
  if (!placed.length && memory.dots.length) {
    placed.push({ dot: memory.dots[0], size: memory.dots[0].sizePx * shrink, at: BADGE_FROM });
  }
  /** Everything the rim is not showing: past the eight it keeps, and past the room the arc has. */
  const extra = memory.totalAll - placed.length;
  return (
    <span className="memory-badges" aria-hidden="true">
      {placed.map(({ dot, size, at }, i) => {
        const rad = (at * Math.PI) / 180;
        const isLast = i === placed.length - 1;
        return (
          <MemoryBadge
            key={dot.id}
            dot={dot}
            size={size}
            x={Math.cos(rad) * radius}
            y={Math.sin(rad) * radius}
            pulse={dot.fresh && !reducedMotion}
            more={isLast && extra > 0 ? extra : 0}
            title={dot.kind === 'story' ? labelStory : labelPhoto}
            labelMore={labelMore}
          />
        );
      })}
    </span>
  );
}

function MemoryBadge({ dot, size, x, y, pulse, more, title, labelMore }: { dot: MemoryDot; size: number; x: number; y: number; pulse: boolean; more: number; title: string; labelMore: (n: number) => string }) {
  // A badge is thirty pixels across at its largest: always the small copy.
  const { src, onError, onLoad } = usePortraitImg(dot.kind === 'photo' ? `portrait:${dot.id}` : dot.kind === 'moment' ? `moment:${dot.id}` : undefined, true);
  return (
    <>
      {/*
        * The pulse of something new is a ring of its own, *beside* the badge: the badge clips what is inside
        * it (that is what makes a photograph round), so the ring cannot be its child. See `.memory-badge-ring`
        * for why it is a ring that grows and fades rather than a shadow that swells.
        */}
      {pulse && <span className="memory-badge-ring" aria-hidden="true" style={{ width: size, height: size, '--bx': `${x}px`, '--by': `${y}px` } as CSSProperties} />}
      <span
        className={`memory-badge is-${dot.kind} ${pulse ? 'fresh' : ''}`}
        title={more ? labelMore(more) : title}
        style={{ width: size, height: size, '--bx': `${x}px`, '--by': `${y}px`, opacity: 0.7 + 0.3 * (1 - dot.settled) } as CSSProperties}
      >
        {more ? (
          <span className="memory-badge-more">+{more}</span>
        ) : dot.kind !== 'story' && src ? (
          <img src={src} alt="" draggable={false} decoding="async" onError={onError} onLoad={onLoad} />
        ) : (
          <Icon name={dot.kind === 'moment' ? 'sparkle' : dot.kind === 'photo' ? 'image' : 'stories'} />
        )}
      </span>
    </>
  );
}
